import { useState } from "react";
import { CheckCircle2, Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { axonLead } from "@/lib/axon";
import { gadsLead } from "@/lib/gads";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { STATE_ABBR } from "@/lib/states";

const US_STATES = Object.keys(STATE_ABBR)
  .map((k) => k.replace(/\b\w/g, (c) => c.toUpperCase()).replace(" Of ", " of "))
  .sort();

const ACCENT = {
  yellow: { border: "border-neon-yellow/60 hover:border-neon-yellow", label: "text-neon-yellow", btn: "bg-neon-yellow text-black hover:bg-white", ring: "focus:border-neon-yellow" },
  magenta: { border: "border-neon-magenta/60 hover:border-neon-magenta", label: "text-neon-magenta", btn: "bg-neon-magenta text-black hover:bg-white", ring: "focus:border-neon-magenta" },
  cyan: { border: "border-neon-cyan/60 hover:border-neon-cyan", label: "text-neon-cyan", btn: "bg-neon-cyan text-black hover:bg-white", ring: "focus:border-neon-cyan" },
};

const field = "w-full bg-black/60 border border-border px-3 py-2.5 text-sm outline-none transition-colors placeholder:text-white/30";

export default function LandingInquiryCard({ id, accent, kicker, title, pitch, buttonLabel, interest, referralCode, withBusinessType = false, href = null, image = null, imageAlt = "", placeholder = null, secondaryHref = null, secondaryLabel = null, defaultOpen = false, defaultMessage = "" }) {
  const a = ACCENT[accent];
  const [open, setOpen] = useState(!!defaultOpen);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ name: "", company: "", phone: "", email: "", state: "", business_type: "", message: defaultMessage || "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.name.trim() || !f.phone.trim() || !f.email.trim()) { setErr("Name, phone and email are required."); return; }
    setBusy(true);
    try {
      const message = withBusinessType && f.business_type.trim()
        ? `Business type: ${f.business_type.trim()}\n\n${f.message.trim()}`.trim()
        : f.message.trim() || null;
      await api.post("/fortune/inquire", {
        name: f.name.trim(), company: f.company.trim() || null, phone: f.phone.trim(), email: f.email.trim(),
        state: f.state || null, interest, message, source: "operator_landing", referral_code: referralCode || null,
      });
      axonLead("operator_landing"); gadsLead();
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail?.[0]?.msg || ex?.response?.data?.detail || "Something went wrong — please call or text us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {placeholder ? (
        <button
          type="button"
          data-testid={`cta-card-${id}`}
          onClick={() => setOpen(true)}
          className={`border border-dashed ${a.border} bg-black/20 p-6 sm:p-8 flex flex-col items-center justify-center text-center gap-4 transition group min-h-[280px]`}
        >
          <span className={`w-16 h-16 border ${a.border} flex items-center justify-center group-hover:bg-white/5 transition`}>
            <Plus className={`w-8 h-8 ${a.label}`} />
          </span>
          <span className="font-heading font-black text-lg sm:text-xl leading-snug max-w-[22ch]">{placeholder}</span>
          <span className={`font-mono text-[11px] uppercase tracking-widest ${a.label}`}>Click here →</span>
        </button>
      ) : (
      <div data-testid={`cta-card-${id}`} className={`border ${a.border} bg-black/40 p-5 sm:p-6 flex flex-col transition`}>
        {image && (
          <div className="mb-4 -mx-1 border border-white/10 bg-black overflow-hidden" style={{ aspectRatio: "8.5 / 11" }}>
            <img src={image} alt={imageAlt} className="w-full h-full object-contain" loading="lazy" />
          </div>
        )}
        <div className={`font-mono text-[10px] uppercase tracking-[0.24em] ${a.label}`}>{kicker}</div>
        <h2 className="mt-2 font-heading font-black text-xl sm:text-2xl leading-tight">{title}</h2>
        <p className="mt-2 text-sm sm:text-base text-white/80 flex-1">{pitch}</p>
        {href ? (
          <div className="mt-5 flex flex-nowrap items-center gap-2">
            <a
              href={href}
              data-testid={`cta-button-${id}`}
              className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1 ${a.btn} transition-colors font-heading font-black px-2.5 py-2.5 uppercase tracking-wider text-[10px] sm:text-[11px] whitespace-nowrap`}
            >
              {buttonLabel}
            </a>
            {secondaryHref && (
              <a
                href={secondaryHref}
                data-testid={`cta-button-secondary-${id}`}
                className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1 border ${a.border} transition-colors font-mono px-2.5 py-2.5 uppercase tracking-wider text-[10px] sm:text-[11px] whitespace-nowrap hover:bg-white/5`}
              >
                {secondaryLabel}
              </a>
            )}
          </div>
        ) : (
          <button
            type="button"
            data-testid={`cta-button-${id}`}
            onClick={() => setOpen(true)}
            className={`mt-5 self-start inline-flex items-center gap-2 ${a.btn} transition-colors font-heading font-black px-4 py-3 uppercase tracking-wider text-xs whitespace-nowrap`}
          >
            {buttonLabel} →
          </button>
        )}
      </div>
      )}

      {href ? null : (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none border-border bg-card" data-testid={`cta-dialog-${id}`}>
          <DialogHeader>
            <DialogTitle className={`font-heading font-black uppercase tracking-tight ${a.label}`}>{title}</DialogTitle>
            <DialogDescription className="text-xs">{done ? "" : "Takes 30 seconds. A real person replies — usually the same day."}</DialogDescription>
          </DialogHeader>
          {done ? (
            <div className="py-8 text-center" data-testid={`cta-thanks-${id}`}>
              <CheckCircle2 className={`w-10 h-10 mx-auto ${a.label}`} />
              <div className="mt-3 font-heading font-black text-lg">Thanks, {f.name.split(" ")[0]} — got it.</div>
              <p className="mt-2 text-sm text-white/70">We'll reach out at {f.phone} or {f.email} shortly. No account needed.</p>
              <button type="button" data-testid={`cta-close-${id}`} onClick={() => setOpen(false)} className="mt-6 font-mono text-[11px] uppercase tracking-widest text-white/60 hover:text-white">Close</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid={`cta-form-${id}`}>
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid={`cta-${id}-name`} className={`${field} ${a.ring}`} placeholder="Your name *" value={f.name} onChange={set("name")} required />
                <input data-testid={`cta-${id}-company`} className={`${field} ${a.ring}`} placeholder="Business / company" value={f.company} onChange={set("company")} />
                <input data-testid={`cta-${id}-phone`} className={`${field} ${a.ring}`} placeholder="Phone *" type="tel" value={f.phone} onChange={set("phone")} required />
                <input data-testid={`cta-${id}-email`} className={`${field} ${a.ring}`} placeholder="Email *" type="email" value={f.email} onChange={set("email")} required />
              </div>
              <select data-testid={`cta-${id}-state`} className={`${field} ${a.ring} text-white/80`} value={f.state} onChange={set("state")}>
                <option value="">State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {withBusinessType && (
                <input data-testid={`cta-${id}-business-type`} className={`${field} ${a.ring}`} placeholder="What kind of business? (e.g. vape wholesale, gas station broker, franchise)" value={f.business_type} onChange={set("business_type")} />
              )}
              <textarea data-testid={`cta-${id}-message`} className={`${field} ${a.ring} min-h-[84px]`} placeholder={withBusinessType ? "What do you want us to sell, and to whom?" : "Anything we should know? (optional)"} value={f.message} onChange={set("message")} />
              {err && <div data-testid={`cta-${id}-error`} className="text-xs text-red-400">{err}</div>}
              <button type="submit" data-testid={`cta-submit-${id}`} disabled={busy} className={`w-full inline-flex items-center justify-center gap-2 ${a.btn} transition-colors font-heading font-black px-5 py-3 uppercase tracking-widest text-sm disabled:opacity-60`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : buttonLabel}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      )}
    </>
  );
}
